import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authService = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (email, password) => apiClient.post('/auth/login', { email, password }),
  getProfile: () => apiClient.get('/auth/profile'),
  updateProfile: (data) => apiClient.put('/auth/profile', data),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => apiClient.post(`/auth/reset-password/${token}`, { password }),
};

export const helperService = {
  getAssignedTasks: (params) => apiClient.get('/helper/tasks', { params }),
  submitVerificationFindings: (taskId, data) => apiClient.post(`/helper/tasks/${taskId}/submit`, data),
  getCommission: (helperId) => apiClient.get(`/helper/commission/${helperId}`),
  getClearanceStatus: (helperId) => apiClient.get(`/helper/clearance/${helperId}`),
};

export const adminService = {
  createTask: (data) => apiClient.post('/admin/tasks/create', data),
  assignTask: (data) => apiClient.post('/admin/tasks/assign', data),
  getUnassignedUsers: () => apiClient.get('/admin/users/unassigned'),
  getVerificationReport: (userId) => apiClient.get(`/admin/report/${userId}`),
  submitVerificationReport: (userId, data) => apiClient.post(`/admin/report/${userId}`, data),
  generatePaymentLink: (data) => apiClient.post('/admin/payment/link', data),
  approveHelperTask: (taskId, data) => apiClient.post(`/admin/tasks/${taskId}/approve`, data),
  rejectHelperTask: (taskId, data) => apiClient.post(`/admin/tasks/${taskId}/reject`, data),
  getTasks: () => apiClient.get('/admin/tasks'),
  getTask: (taskId) => apiClient.get(`/admin/tasks/${taskId}`),
  completeTask: (taskId) => apiClient.put(`/admin/tasks/${taskId}/complete`),
  rejectTask: (taskId, data) => apiClient.put(`/admin/tasks/${taskId}/reject`, data),
  getHelpers: (params) => apiClient.get('/admin/helpers', { params }),
  createHelper: (data) => apiClient.post('/admin/helpers', data),
  getStats: () => apiClient.get('/admin/stats'),
  getActivePaymentAccounts: () => apiClient.get('/admin/payment-accounts'),
  getCommissions: (params) => apiClient.get('/admin/commissions', { params }),
  approveCommission: (commissionId, data) => apiClient.put(`/admin/commissions/${commissionId}/approve`, data),
  markCommissionPaid: (commissionId, data) => apiClient.put(`/admin/commissions/${commissionId}/mark-paid`, data),
  getEligibleCertificateClients: () => apiClient.get('/admin/certificates/eligible'),
  submitCertificate: (formData) => apiClient.post('/admin/certificates', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getTestimonials: () => apiClient.get('/admin/testimonials'),
  createTestimonial: (formData) => apiClient.post('/admin/testimonials', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateTestimonial: (id, formData) => apiClient.put(`/admin/testimonials/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteTestimonial: (id) => apiClient.delete(`/admin/testimonials/${id}`),
};

export const publicService = {
  getTestimonials: () => apiClient.get('/public/testimonials'),
  submitContactMessage: (data) => apiClient.post('/public/contact', data),
};

export const superAdminService = {
  approveHelper: (helperId) => apiClient.post(`/superadmin/helper/${helperId}/approve`),
  rejectHelper: (helperId, data) => apiClient.post(`/superadmin/helper/${helperId}/reject`, data),
  approveCertificate: (certificateId) => apiClient.post(`/superadmin/certificate/${certificateId}/approve`),
  rejectCertificate: (certificateId, data) => apiClient.post(`/superadmin/certificate/${certificateId}/reject`, data),
  getPendingApprovals: (config) => apiClient.get('/superadmin/pending', config),
  getSystemStats: () => apiClient.get('/superadmin/stats'),
  getAuditLogs: () => apiClient.get('/superadmin/audit-logs'),
  getUsers: (params) => apiClient.get('/superadmin/users', { params }),
  getAdminUsers: (params) => apiClient.get('/superadmin/admins', { params }),
  createAdmin: (data) => apiClient.post('/superadmin/admins', data),
  getHelperUsers: (params) => apiClient.get('/superadmin/helpers', { params }),
  createHelper: (data) => apiClient.post('/superadmin/helpers', data),
  getPendingHelpers: () => apiClient.get('/superadmin/helpers/pending'),
  getTasks: () => apiClient.get('/superadmin/tasks'),
  getTask: (taskId) => apiClient.get(`/superadmin/tasks/${taskId}`),
  completeTask: (taskId) => apiClient.put(`/superadmin/tasks/${taskId}/complete`),
  rejectTask: (taskId, data) => apiClient.put(`/superadmin/tasks/${taskId}/reject`, data),
  approveHelperApproval: (helperId) => apiClient.put(`/superadmin/helpers/${helperId}/approve`),
  rejectHelperApproval: (helperId) => apiClient.put(`/superadmin/helpers/${helperId}/reject`),
  deleteHelper: (helperId) => apiClient.delete(`/superadmin/helpers/${helperId}`),
  getCommissions: (params) => apiClient.get('/superadmin/commissions', { params }),
  approveCommission: (commissionId, data) => apiClient.put(`/superadmin/commissions/${commissionId}/approve`, data),
  markCommissionPaid: (commissionId, data) => apiClient.put(`/superadmin/commissions/${commissionId}/mark-paid`, data),
  getAllVerificationRequests: (config) => apiClient.get('/admin/verification', config),
  requestVerificationPayment: (id, data) => apiClient.post(`/admin/verification/${id}/request-payment`, data),
  markVerificationPaid: (id) => apiClient.post(`/admin/verification/${id}/mark-paid`),
  getPaymentAccounts: () => apiClient.get('/superadmin/payment-accounts'),
  createPaymentAccount: (data) => apiClient.post('/superadmin/payment-accounts', data),
  togglePaymentAccount: (id) => apiClient.put(`/superadmin/payment-accounts/${id}/toggle`),
  getEligibleCertificateClients: () => apiClient.get('/superadmin/certificates/eligible'),
  submitCertificate: (formData) => apiClient.post('/superadmin/certificates', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getCertificate: (certificateId) => apiClient.get(`/superadmin/certificate/${certificateId}`),
  getContactMessages: (config) => apiClient.get('/superadmin/contact-messages', config),
  markContactMessageRead: (id) => apiClient.put(`/superadmin/contact-messages/${id}/read`),
  deleteContactMessage: (id) => apiClient.delete(`/superadmin/contact-messages/${id}`),
};

export const userService = {
  getMyRequests: (config) => apiClient.get('/auth/verification', config),
  getMyCertificates: (config) => apiClient.get('/auth/certificates', config),
  downloadCertificate: (certificateId) => apiClient.post(`/auth/certificates/${certificateId}/download`),
};

export default apiClient;
